// Design tokens from dashboard mockup CSS variables
// Created as part of ENG2-1698: Create Unified MUI v5 Theme
// Updated to support dark mode

export const tokens = {
  colors: {
    white: '#FFFFFF',
    black: '#000000',
    primary: {
      900: '#996300',
      800: '#B37400',
      700: '#CC8500',
      600: '#E69500',
      500: '#FFC000',
      400: '#FFCC33',
      300: '#FFD966',
      200: '#FFE699',
      100: '#FFF2CC',
      50: '#FFFBF0',
    },
    accent: {
      500: '#FFB75A',
      400: '#FFCC89',
      300: '#FFDDB0',
      100: '#FFF3E0',
    },
    success: {
      700: '#1B5E20',
      600: '#2E7D32',
      500: '#4CAF50',
      300: '#81C784',
      100: '#E8F5E9',
      50: '#F1F8E9',
    },
    warning: {
      700: '#8C6700',
      600: '#B8860B',
      500: '#FFA000',
      300: '#FFD54F',
      100: '#FFF8E1',
      50: '#FFFDE7',
    },
    error: {
      700: '#B71C1C',
      600: '#C62828',
      500: '#DC182F',
      300: '#EF5350',
      100: '#FFEBEE',
      50: '#FFEBEE',
    },
    info: {
      500: '#1976D2',
      100: '#E3F2FD',
    },
    grey: {
      900: '#1A1D21',
      800: '#353A3F',
      700: '#4A5056',
      600: '#6B7280',
      500: '#9CA3AF',
      400: '#C4C9CF',
      300: '#DFE2E6',
      200: '#ECEEF0',
      100: '#F6F7F8',
      50: '#FAFBFC',
    },
  },
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
  },
  radius: {
    sm: 6,
    md: 8,
    lg: 12,
    xl: 16,
  },
  shadows: {
    xs: '0 1px 2px rgba(0,0,0,0.05)',
    sm: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
    md: '0 4px 6px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.04)',
    lg: '0 10px 15px rgba(0,0,0,0.08), 0 4px 6px rgba(0,0,0,0.04)',
  },
  // Dark mode shadows (more subtle on dark backgrounds)
  darkShadows: {
    xs: '0 1px 2px rgba(0,0,0,0.2)',
    sm: '0 1px 3px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)',
    md: '0 4px 6px rgba(0,0,0,0.3), 0 2px 4px rgba(0,0,0,0.2)',
    lg: '0 10px 15px rgba(0,0,0,0.4), 0 4px 6px rgba(0,0,0,0.2)',
  },
  layout: {
    sidebarWidth: 260,
    sidebarCollapsedWidth: 72,
    topbarHeight: 64,
  },
  transitions: {
    fast: '150ms ease',
    normal: '250ms ease',
  },
} as const;

// Dark mode specific colors - Simplified 2-shade system
// Shade 1 (#121212): Sidebar only (darkest)
// Shade 2 (#1E1E1E): Everything else (content, cards, tables, inputs, dialogs)
export const darkModeColors = {
  background: {
    default: '#1E1E1E', // Main content background
    paper: '#1E1E1E', // Cards, tables, dialogs - unified with default
    sidebar: '#121212', // Sidebar only - darkest element
  },
  // Interactive state overlays (apply on top of background.paper)
  hover: 'rgba(255, 255, 255, 0.04)',
  selected: 'rgba(255, 255, 255, 0.08)',
  border: {
    primary: 'rgba(255, 255, 255, 0.12)',
    secondary: 'rgba(255, 255, 255, 0.16)',
  },
  text: {
    primary: '#FFFFFF',
    secondary: '#B3B3B3',
    disabled: '#666666',
  },
} as const;

// Light mode specific colors (for explicit reference)
export const lightModeColors = {
  background: {
    default: tokens.colors.grey[100],
    paper: '#FFFFFF',
    elevated: '#FFFFFF',
  },
  surface: {
    primary: '#FFFFFF',
    secondary: tokens.colors.grey[50],
    tertiary: tokens.colors.grey[100],
  },
  border: {
    primary: tokens.colors.grey[200],
    secondary: tokens.colors.grey[300],
  },
  text: {
    primary: tokens.colors.grey[800],
    secondary: tokens.colors.grey[600],
    disabled: tokens.colors.grey[400],
  },
} as const;

export type Tokens = typeof tokens;
